import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Clock, Loader2, PackagePlus } from "lucide-react";
import { WhatsAppConnection } from "@/components/WhatsAppConnection";

const AUTO = [["budget_ready", "Orçamento pronto (com link)"], ["car_ready", "Carro pronto para retirada"], ["revision", "Lembrete de revisão (7 dias antes)"],
  ["booking_reminder", "Véspera do agendamento"], ["birthday", "Aniversário"]];
const VARS = ["cliente", "placa", "os", "link", "oficina", "valor", "data"];

function TplBox({ k, label, value, onChange, enabled, onToggle, meta, tname, onTname }) {
  const ref = useRef(null);
  const insert = (v) => {
    const el = ref.current; const tok = `{${v}}`;
    const s = el?.selectionStart ?? value.length, e = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, s) + tok + value.slice(e));
  };
  return (
    <div data-testid={`wa-tpl-${k}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-zinc-100">{label}</span>
        <label className="flex items-center gap-2 text-xs text-zinc-400">Automático<Switch data-testid={`wa-auto-${k}`} checked={enabled} onCheckedChange={onToggle} /></label>
      </div>
      {meta && <label className="flex items-center gap-2 text-xs text-zinc-400">Modelo aprovado na Meta
        <Input data-testid={`wa-meta-tpl-${k}`} value={tname} onChange={(e) => onTname(e.target.value)} placeholder="ex.: orcamento_pronto" className="h-8 bg-zinc-900 border-zinc-800 text-xs flex-1" /></label>}
      <textarea ref={ref} data-testid={`wa-tpl-${k}-text`} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full rounded-md bg-zinc-900 border border-zinc-800 p-2 text-sm text-zinc-100" />
      <div className="flex flex-wrap gap-1">{VARS.map((v) => <button key={v} type="button" data-testid={`wa-var-${k}-${v}`} onClick={() => insert(v)} className="text-[11px] font-mono rounded-full border border-zinc-700 px-2 py-0.5 text-green-300 hover:bg-zinc-800">{`{${v}}`}</button>)}</div>
    </div>
  );
}

function WaLogs() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/shop-settings/whatsapp-auto/logs").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows?.length) return null;
  return (
    <div data-testid="wa-logs" className="rounded-xl border border-zinc-800 p-3 space-y-1">
      <div className="text-[11px] font-mono-caps text-zinc-500">Últimos envios</div>
      {rows.map((r, i) => <div key={i} className="flex justify-between gap-2 text-xs"><span className="text-zinc-300 truncate">{new Date(r.at).toLocaleString("pt-BR")} · {r.kind} · {r.to || "—"}</span>
        <span className={r.status === "enviado" ? "text-green-400" : "text-red-400"} title={r.error || ""}>{r.status}</span></div>)}
    </div>
  );
}

function IntakeWaToggle() {
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(user?.shop?.intake_whatsapp_enabled !== false);
  const [busy, setBusy] = useState(false);
  const save = async (val) => {
    setBusy(true);
    try { await api.put("/shop-settings/intake-whatsapp", { enabled: val }); setEnabled(val); refresh?.(); toast.success("Preferência salva"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="intake-wa-toggle" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 flex items-start sm:items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-sm font-bold text-zinc-100">Enviar checklist de entrada ao cliente</div>
        <p className="text-[11px] text-zinc-500">Mostra o botão “Enviar checklist pelo WhatsApp” na OS. O PDF leva fotos reduzidas para caber no WhatsApp.</p>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input data-testid="intake-wa-toggle-input" type="checkbox" checked={enabled} disabled={busy} onChange={(e) => save(e.target.checked)} className="h-5 w-5 accent-green-brand" />
        <span className="text-xs text-zinc-300">{enabled ? "Ligado" : "Desligado"}</span>
      </label>
    </div>
  );
}

function GroupLaborPdfToggle() {
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.shop?.group_labor_pdf_default);
  const [busy, setBusy] = useState(false);
  const save = async (val) => {
    setBusy(true);
    try { await api.put("/shop-settings/group-labor-pdf", { enabled: val }); setEnabled(val); refresh?.(); toast.success("Preferência salva"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="group-labor-pdf-toggle" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 flex items-start sm:items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-sm font-bold text-zinc-100">Agrupar mão de obra no PDF por padrão</div>
        <p className="text-[11px] text-zinc-500">Toda OS nova já vem com a opção ligada. Você pode desligar em cada OS. Só muda o que o cliente vê no PDF; cálculos, comissões e relatórios não mudam.</p>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input data-testid="group-labor-pdf-toggle-input" type="checkbox" checked={enabled} disabled={busy} onChange={(e) => save(e.target.checked)} className="h-5 w-5 accent-green-brand" />
        <span className="text-xs text-zinc-300">{enabled ? "Ligado" : "Desligado"}</span>
      </label>
    </div>
  );
}


export function WhatsAppSettings() {
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/shop-settings/whatsapp-auto").then(({ data }) => setCfg(data)).catch((e) => toast.error(formatApiError(e))); }, [user?.shop?.id]);
  if (!cfg) { return <Loader2 className="h-5 w-5 animate-spin text-green-brand" />; }
  const upd = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/shop-settings/whatsapp-auto/messages", { enabled: cfg.enabled, meta_templates: cfg.meta_templates });
      await api.put("/shop-settings/messages", { templates: { ...(user?.shop?.wa_templates || {}), ...cfg.texts } });
      toast.success("WhatsApp salvo"); refresh?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="whatsapp-settings" className="rounded-2xl border border-card2-border bg-card2 p-5 space-y-4">
      <div className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-emerald-400" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">WhatsApp automático</h2></div>
      {!cfg.available && <div data-testid="wa-addon-only" className="rounded-lg border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-sm text-amber-200 flex items-center justify-between gap-2 flex-wrap">
        <span>Disponível como adicional. Os botões manuais de WhatsApp continuam funcionando normalmente.</span>
        <Link to="/owner/pacotes" data-testid="wa-addon-link" className="inline-flex items-center gap-1 text-amber-300 font-bold"><PackagePlus className="h-4 w-4" /> Pacotes adicionais</Link></div>}
      <WhatsAppConnection conn={cfg.conn} disabled={!cfg.available} onSaved={(c) => upd("conn", c)} />
      <IntakeWaToggle />
      <GroupLaborPdfToggle />
      <p className="flex items-center gap-2 text-xs text-zinc-400"><Clock className="h-4 w-4" /> Orçamento e carro pronto saem na hora; revisão, véspera e aniversário saem às 9h (Brasília). Os textos também valem para os botões manuais.</p>
      <div className="space-y-3">
        {AUTO.map(([k, l]) => <TplBox key={k} k={k} label={l} value={cfg.texts[k] || ""} onChange={(v) => upd("texts", { ...cfg.texts, [k]: v })}
          enabled={!!cfg.enabled[k]} onToggle={(v) => upd("enabled", { ...cfg.enabled, [k]: v })}
          meta={cfg.conn.provider === "meta"} tname={cfg.meta_templates[k] || ""} onTname={(v) => upd("meta_templates", { ...cfg.meta_templates, [k]: v })} />)}
      </div>
      {cfg.conn.provider === "meta" && <p className="text-[11px] text-zinc-500">Na Meta é enviado o modelo aprovado, com as variáveis nesta ordem: nome, placa, nº OS, link.</p>}
      <Button data-testid="wa-save" onClick={save} disabled={busy} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar mensagens"}</Button>
      <WaLogs />
    </section>
  );
}
